import * as React from "react";
import careerOneStopLogo from "./logoimages/CareerOneStop_logo.jpeg";
import medhtechLogo from "./logoimages/Medhtech-dark.png";

type FooterLink = {
  label: string;
  href: string;
  onClick?: () => void;
  target?: string;
};

interface FooterProps {
  onAboutClick?: () => void;
  onContactClick?: () => void;
  onQuickStartClick?: () => void;
}

export function Footer({ onAboutClick, onContactClick, onQuickStartClick }: FooterProps) {
  // auto year update
  React.useEffect(() => {
    const year = new Date().getFullYear();
    const yearElement = document.getElementById("cp-year");
    if (yearElement) yearElement.textContent = year.toString();
  }, []);

  const quickStartGuideUrl = process.env.REACT_APP_QUICK_START_GUIDE_URL;

  const handleRestartTour = React.useCallback(() => {
    // Clear the cookie so tour runs from the beginning
    document.cookie = `demoTourCompleted=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    window.dispatchEvent(new CustomEvent('startDemoTour', { detail: { source: 'footer' } }));
  }, []);

  const quickStartLink: FooterLink = onQuickStartClick
    ? { label: "Quick Start Guide", href: "#", onClick: onQuickStartClick }
    : quickStartGuideUrl
      ? { label: "Quick Start Guide", href: quickStartGuideUrl, target: "_blank" }
      : { label: "Quick Start Guide", href: "#", onClick: () => alert("Quick Start Guide is coming soon!") };

  const footerLinks: FooterLink[] = [
    { label: "About", href: "#", onClick: onAboutClick },
    { label: "Privacy", href: "#", onClick: () => alert("Privacy policy is being developed to outline how we handle and protect user information.") },
    { label: "Contact", href: "#", onClick: onContactClick },
    { label: "Feedback", href: "https://forms.office.com/Pages/ResponsePage.aspx?id=EByzxFUNhUip6u1tDIKBJHTLTd8F4zhDtql0HAq0FzJUMEFGRTdBSjJKNldWM1lSTVpKMlFIS1o2QiQlQCN0PWcu", target: "_blank" },
    { label: "Restart Tour", href: "#", onClick: handleRestartTour },
    quickStartLink
  ];

  return (
    <footer
      aria-label="Site footer"
      className="bg-[#1b2c5a] text-white print:bg-white print:text-black border-t border-[#162844]"
    >
      <div className="mx-auto max-w-7xl px-6 py-14 lg:px-8">
                 {/* top row: brand + nav */}
         <div className="flex flex-col gap-10 border-b border-[#707070]/40 pb-10 md:flex-row md:items-center md:justify-between">
           <div className="flex items-center gap-4 group">
             {/* Forward Sumner Logo */}
             <div className="relative overflow-hidden rounded-lg bg-white/10 p-2 backdrop-blur-sm transition-all duration-300 group-hover:bg-white/20">
               <img
                 src="/FSEP_CPP_Logo.png"
                 alt="Forward Sumner Logo"
                 className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
               />
             </div>
             <div>
               <p
                 className="text-xl font-['Anton'] leading-6 text-white"
                 style={{ letterSpacing: "0.5px" }}
               >
                 Career Pathways Portal
               </p>
               <p className="text-sm font-['Roboto'] text-[#a6a6a6] mt-1">
                 Empowering career choices with local data that matters
               </p>
             </div>
           </div>

           {/* nav */}
           <nav aria-label="Footer navigation" className="flex-shrink-0">
             <ul className="flex flex-wrap gap-x-8 gap-y-4 text-sm font-['Roboto']">
             {footerLinks.map((link) => (
                 <li key={link.label}>
                   <a
                     className="relative text-[#e6e6e6] transition-colors duration-200 hover:text-[#d5a32a] after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-[#d5a32a] after:transition-all after:duration-300 hover:after:w-full cursor-pointer"
                     href={link.href}
                     target={link.target}
                     rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                     onClick={link.onClick ? (e) => { e.preventDefault(); link.onClick?.(); } : undefined}
                   >
                     {link.label}
                   </a>
                 </li>
               ))}
             </ul>
           </nav>
         </div>

         {/* data sources section - moved to follow tagline */}
         <div className="mt-8 border-b border-[#707070]/40 pb-8">
           <h2 className="text-sm font-bold uppercase tracking-widest text-[#f2c94c] font-['Roboto_Condensed'] mb-6">
             Data Sources & Acknowledgments
           </h2>
           <div className="flex flex-wrap items-center gap-x-10 gap-y-6">
            {/* CareerOneStop */}
            <a
              href="https://www.careeronestop.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 p-3 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-white/20 hover:scale-105"
              aria-label="CareerOneStop - Visit CareerOneStop.org"
            >
              <div className="relative overflow-hidden rounded-md bg-white p-2">
                <img
                  src={careerOneStopLogo}
                  alt="CareerOneStop"
                  className="h-8 w-auto transition-transform duration-300 group-hover:scale-110"
                />
              </div>
            </a>

            {/* O*NET */}
            <a
              href="https://www.onetonline.org/"
              target="_blank"
              rel="noopener"
              className="group inline-flex items-center gap-3 p-3 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 transition-all duration-300 hover:bg-white/10 hover:border-white/20 hover:scale-105"
              aria-label="O*NET OnLine"
            >
              <div className="relative overflow-hidden rounded-md bg-white p-2">
                <img
                  src="https://www.onetonline.org/image/online-header.svg"
                  alt="O*NET OnLine"
                  className="h-8 w-auto transition-transform duration-300 group-hover:scale-110"
                />
              </div>
            </a>

            {/* BLS */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-[#f2e8d5]/10 border border-white/10">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/7/76/Bureau_of_Labor_Statistics_logo.svg"
                alt="Bureau of Labor Statistics"
                className="h-8 w-auto"
              />
              <span className="text-sm text-[#e6e6e6] font-['Roboto']">
                Data include estimates from the{" "}
                <a
                  href="https://www.bls.gov/"
                  target="_blank"
                  rel="noopener"
                  className="ml-1 underline underline-offset-2 text-[#76b6f2] hover:text-[#d5a32a]"
                >
                  U.S. Bureau of Labor Statistics (BLS)
                </a>
                .
              </span>
            </div>
                     </div>
         </div>

         {/* legal */}
         <div className="mt-8 pt-8">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 font-['Roboto'] text-sm">
            <div>
              <p className="text-[#a6a6a6]">
                © <span id="cp-year" className="font-semibold text-white">2025</span> Forward
                Sumner Economic Partnership.
                <span className="block mt-1">All rights reserved.</span>
              </p>
            </div>
            <div>
              <p className="text-xs text-[#a6a6a6]">
                <span className="font-semibold text-white">O*NET®</span> is a
                trademark of the U.S. Department of Labor, Employment and
                Training Administration (USDOL/ETA). Use of O*NET content and
                trademarks is pursuant to O*NET licensing and attribution
                requirements.
              </p>
            </div>
            <div>
              <p className="text-xs text-[#a6a6a6]">
                <span className="font-semibold text-white">CareerOneStop</span>{" "}
                is sponsored by USDOL/ETA. Third-party marks do not imply
                endorsement.
              </p>
            </div>
          </div>
        </div>

        {/* Website credit */}
        <div className="mt-6 pt-6 border-t border-[#707070]/20">
          <div className="text-center">
            <div className="text-xs text-[#a6a6a6] font-['Roboto'] flex items-center justify-center gap-2">
              Website by{" "}
              <a
                href="https://www.medhtech.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[#d5a32a] hover:text-white transition-colors duration-200 underline underline-offset-2 flex items-center gap-2"
              >
                <div className="bg-white p-1 rounded">
                  <img
                    src={medhtechLogo}
                    alt="MedTech LLC"
                    className="h-6 w-auto"
                  />
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
